import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Subscriber {
  id: string;
  email: string;
  created_at: string;
}

interface Product {
  id: string;
  name: string;
  price: number;
  original_price?: number;
  image_url: string;
  category: string;
  is_new: boolean;
  is_on_sale: boolean;
}

export default function NewsletterManager() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado del formulario de envío
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    includeNewProducts: false,
    includeOnSaleProducts: false
  });

  useEffect(() => {
    loadSubscribers();
    loadProducts();
  }, []);

  const loadSubscribers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubscribers(data || []);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const deleteSubscriber = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este suscriptor?')) return;

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSubscribers(subscribers.filter(sub => sub.id !== id));
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      alert('Error al eliminar el suscriptor');
    }
  };

  const sendNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (subscribers.length === 0) {
      alert('No hay suscriptores para enviar el newsletter');
      return;
    }

    if (!emailData.subject.trim() || !emailData.message.trim()) {
      alert('Por favor completa el asunto y el mensaje');
      return;
    }

    setIsSending(true);

    try {
      // Obtener productos según las opciones seleccionadas
      let selectedProducts: Product[] = [];
      
      if (emailData.includeNewProducts) {
        selectedProducts = [...selectedProducts, ...products.filter(p => p.is_new)];
      }
      
      if (emailData.includeOnSaleProducts) {
        selectedProducts = [...selectedProducts, ...products.filter(p => p.is_on_sale)];
      }

      // Eliminar duplicados
      selectedProducts = selectedProducts.filter((product, index, self) => 
        index === self.findIndex(p => p.id === product.id)
      );

      // Llamar a la función de Supabase para enviar el newsletter
      const { data, error } = await supabase.functions.invoke('send-newsletter', {
        body: {
          subscribers: subscribers.map(sub => sub.email),
          subject: emailData.subject,
          message: emailData.message,
          products: selectedProducts
        }
      });

      if (error) throw error;

      alert(`Newsletter enviado exitosamente a ${subscribers.length} suscriptores`);
      
      // Resetear formulario
      setEmailData({
        subject: '',
        message: '',
        includeNewProducts: false,
        includeOnSaleProducts: false
      });

    } catch (error: any) {
      console.error('Error sending newsletter:', error);
      alert(`Error al enviar el newsletter: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const filteredSubscribers = subscribers.filter(subscriber =>
    subscriber.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const newProducts = products.filter(p => p.is_new);
  const onSaleProducts = products.filter(p => p.is_on_sale);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#C6A45C] animate-spin mb-4"></i>
          <p className="text-gray-400">Cargando suscriptores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
          Newsletter
        </h2>
        <button 
          onClick={loadSubscribers}
          className="bg-[#C6A45C] text-black px-4 py-2 rounded-lg hover:bg-[#B8965A] transition-colors font-medium flex items-center space-x-2 whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          <span>Actualizar</span>
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-800">
              <i className="ri-mail-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Total Suscriptores</p>
              <p className="text-2xl font-bold text-white">{subscribers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-800">
              <i className="ri-star-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Productos Nuevos</p>
              <p className="text-2xl font-bold text-white">{newProducts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-800">
              <i className="ri-price-tag-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">En Oferta</p>
              <p className="text-2xl font-bold text-white">{onSaleProducts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-800">
              <i className="ri-send-plane-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Listos para Enviar</p>
              <p className="text-2xl font-bold text-white">{newProducts.length + onSaleProducts.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Send Newsletter Form */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Enviar Newsletter</h3>
        
        <form onSubmit={sendNewsletter} className="space-y-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Asunto del Email *
            </label>
            <input 
              type="text"
              value={emailData.subject}
              onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300"
              placeholder="Ej: ¡Nuevos productos y ofertas especiales!"
              required
            />
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Mensaje Principal *
            </label>
            <textarea 
              value={emailData.message}
              onChange={(e) => setEmailData({...emailData, message: e.target.value})}
              rows={4}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 resize-none"
              placeholder="Escribe el mensaje principal del newsletter..."
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center space-x-3 cursor-pointer bg-gray-800 p-4 rounded-lg border border-gray-600 hover:border-[#C6A45C] transition-colors">
              <input
                type="checkbox"
                checked={emailData.includeNewProducts}
                onChange={(e) => setEmailData({...emailData, includeNewProducts: e.target.checked})}
                className="w-5 h-5 text-[#C6A45C] bg-gray-700 border-gray-600 rounded focus:ring-[#C6A45C] focus:ring-2"
              />
              <div>
                <span className="text-white font-medium">Incluir Productos Nuevos</span>
                <p className="text-gray-400 text-sm">{newProducts.length} productos disponibles</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer bg-gray-800 p-4 rounded-lg border border-gray-600 hover:border-[#C6A45C] transition-colors">
              <input
                type="checkbox"
                checked={emailData.includeOnSaleProducts}
                onChange={(e) => setEmailData({...emailData, includeOnSaleProducts: e.target.checked})}
                className="w-5 h-5 text-[#C6A45C] bg-gray-700 border-gray-600 rounded focus:ring-[#C6A45C] focus:ring-2"
              />
              <div>
                <span className="text-white font-medium">Incluir Productos en Oferta</span>
                <p className="text-gray-400 text-sm">{onSaleProducts.length} productos disponibles</p>
              </div>
            </label>
          </div>

          {/* Preview de productos seleccionados */}
          {(emailData.includeNewProducts || emailData.includeOnSaleProducts) && (
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
              <h4 className="text-white font-medium mb-3">Vista previa de productos a incluir:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                {emailData.includeNewProducts && newProducts.map(product => (
                  <div key={`new-${product.id}`} className="flex items-center space-x-3 bg-gray-700 p-3 rounded">
                    <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{product.name}</p>
                      <p className="text-[#C6A45C] text-sm">{formatPrice(product.price)}</p>
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">NUEVO</span>
                    </div>
                  </div>
                ))}
                {emailData.includeOnSaleProducts && onSaleProducts.map(product => (
                  <div key={`sale-${product.id}`} className="flex items-center space-x-3 bg-gray-700 p-3 rounded">
                    <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{product.name}</p>
                      <div className="flex items-center space-x-2">
                        <p className="text-[#C6A45C] text-sm">{formatPrice(product.price)}</p>
                        {product.original_price && (
                          <p className="text-gray-400 text-xs line-through">{formatPrice(product.original_price)}</p>
                        )}
                      </div>
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">OFERTA</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4">
            <p className="text-gray-400 text-sm">
              Se enviará a {subscribers.length} suscriptores
            </p>
            <button 
              type="submit"
              disabled={isSending || subscribers.length === 0}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all duration-300 font-medium flex items-center space-x-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  <span>Enviar Newsletter</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Subscribers List */}
      <div className="bg-gray-900 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Suscriptores ({subscribers.length})</h3>
          
          <div className="w-64">
            <div className="relative">
              <input 
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 text-sm"
                placeholder="Buscar por email..."
              />
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Fecha de Suscripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredSubscribers.map((subscriber) => (
                <tr key={subscriber.id} className="hover:bg-gray-800 transition-colors duration-300">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-[#C6A45C] rounded-full flex items-center justify-center mr-3">
                        <i className="ri-mail-line text-black text-sm"></i>
                      </div>
                      <span className="text-white">{subscriber.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatDate(subscriber.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button 
                      onClick={() => deleteSubscriber(subscriber.id)}
                      className="text-red-400 hover:text-red-300 transition-colors duration-300"
                      title="Eliminar suscriptor"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredSubscribers.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-mail-line text-6xl text-gray-600 mb-4"></i>
            <p className="text-gray-400 text-lg">
              {searchTerm ? 'No se encontraron suscriptores' : 'No hay suscriptores aún'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}